import { useTranslation } from 'react-i18next'

import { ArcadeModalPortalProps } from '@cash-design-system/react'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { useExecuteOnce } from '@cash-web/shared-util-execute-once'
import { WebViewViewComponent } from '@squareup/customer-data-events-web'

import { ModalPortalProps } from '../ModalPortal/ModalPortal'

/**
 * Hook for shared button props for Modal components.
 */
export const useModalSharedButtonProps = ({
  closeButtonProps,
  backButtonProps,
}: Pick<ModalPortalProps, 'closeButtonProps' | 'backButtonProps'>): Pick<
  ArcadeModalPortalProps,
  'closeButtonProps' | 'backButtonProps'
> => {
  const { t } = useTranslation()

  return {
    closeButtonProps: closeButtonProps?.onClose
      ? {
          ...closeButtonProps,
          onClose: closeButtonProps.onClose,
          ariaLabel: closeButtonProps?.ariaLabel ?? t('Close modal', { context: 'aria label on a button' }),
        }
      : undefined,
    backButtonProps: backButtonProps?.onBack
      ? {
          ...backButtonProps,
          onBack: backButtonProps.onBack,
          ariaLabel:
            backButtonProps?.ariaLabel ?? t('Go back', { context: 'aria label to go back to the last screen' }),
        }
      : undefined,
  }
}

/**
 * Hook for shared props for Modal components.
 */
export const useModalSharedProps = ({
  closeButtonProps,
  backButtonProps,
  ...props
}: ModalPortalProps): ArcadeModalPortalProps => {
  const { t } = useTranslation()
  const logCdfEvent = useLogCashWebCdfEvent()
  const modalSharedButtonProps = useModalSharedButtonProps({ closeButtonProps, backButtonProps })

  useExecuteOnce(() => {
    logCdfEvent(WebViewViewComponent({ componentName: 'Modal', componentType: 'Open' }))
  })

  return {
    ariaLabel: t('Modal', { context: 'aria label for modal dialog' }),
    ...modalSharedButtonProps,
    ...props,
  }
}
